import type { Chapter, Profession, ProfessionTag } from './types.js';
import { THIRTY, THIRTY_POSTS, thirtyGame, thirtyPlace } from './thirty.js';
import type { GameSet, Slot, ThirtyId } from './thirty.js';

/**
 * Mini-games.
 *
 * The mechanics ROTATE. Every post plays a different game each chapter, and within
 * any chapter all six posts are playing six different games at once — a Latin square
 * over (post × chapter). Two consequences, both deliberate:
 *
 *  - nobody repeats a game all night, so the fifth task is as fresh as the first;
 *  - two crew holding the same post are never playing the same thing at the same
 *    time, which is what kept the table feeling identical.
 *
 * Every era is as easy as the first (Marek's test evening, 2026-09-28: the games were "way too
 * fast and impossible"). A guest with a drink in the other hand, in low light, finishes any of
 * them in well under a minute; the eras differ in what the game looks like, not how hard it is.
 *
 * Nothing here gates the show: the drive is floored by the wall clock, so losing is
 * harmless and every game is built to be daft rather than demanding.
 */

export const GAME_RULES = {
  /**
   * Navigator — one touch lifts, gravity pulls. Thread the gap. A gate sets off from
   * `gateStartX` every `gateEvery` ticks and is judged as it reaches the ship at `shipX`.
   * Gentle on purpose: a tap lifts the ship about a fifth of the field and it sinks no faster
   * than `maxFall`, so one easy tap a second or so keeps it level. Nothing moves before the
   * first tap, the gates wait while nobody taps, and the top and bottom edges only stop the ship.
   */
  thread: {
    tickMs: 16,
    lift: -1.55,
    gravity: 0.06,
    maxFall: 0.9,
    shipY: 0.5,
    gateEvery: 110,
    gateStartX: 1.05,
    shipX: 0.18,
    /** How far one gate's opening may sit from the last, so the next is always within reach. */
    drift: 0.3,
    /**
     * No tap for this long and the gates wait too: a guest who puts the phone down to drink comes
     * back to the same gate, and nobody finishes the game by leaving the ship on the floor.
     */
    restMs: 3_000,
  },
  /** Engineer — pulses run to the line; tap on the beat. A generous window, and slow pulses. */
  rhythm: { hitWindowMs: 320, travelMs: 2_400 },
  /** Biotechnician — steer a vine; it grows behind you. */
  vine: { cols: 11, rows: 9 },
  /**
   * Signal Officer — a pattern plays, tap it back. A wrong pad plays it again after `retryMs`.
   * Three rounds (Marek, 2026-09-29), each one light longer and played faster: `roundSpeeds`
   * scales the light and the gap, and `roundPauseMs` is the ROUND 2 — FASTER card between them.
   */
  echo: { padCount: 4, gapMs: 260, leadMs: 600, retryMs: 900, rounds: 3, roundSpeeds: [1, 0.8, 0.65], roundPauseMs: 1_300 },
  /** Archivist — flip artifacts, match the pairs. */
  recall: { flipBackMs: 1_100, matchMs: 260 },
  /**
   * Quartermaster — tap to cut a fair share. Each bar is `barWidth` of its track and slides
   * from side to side on its own, one full swing every `swingMs` (Marek, 2026-09-29: it was far
   * too easy standing still). The fair zone rides on the bar, `tolerance` of the bar either side.
   */
  divide: { tolerance: 0.12, barWidth: 0.62, swingMs: [3_200, 4_000, 2_700] },
  /**
   * 2617, under the sand — a candle in the dark. The light is a circle round the finger,
   * `lightRadius` of the board's width; a tap within `reach` of a lit mushroom picks it.
   */
  forage: { lightRadius: 0.26, reach: 0.1 },
  /** The social one: a 4-digit code read back, face to face. */
  relay: { codeMin: 1000, codeMax: 9999 },
} as const;

interface Base {
  tag: ProfessionTag;
  label: string;
  hint: string;
  /** The thirty only: the first holder's game at this stop, or a second holder's. */
  slot?: Slot;
  /** The thirty only: the title in bokmål (the classic set's are in the guest app). */
  labelNo?: string;
  /** The thirty only: which of the thirty this is. */
  game?: ThirtyId;
  /**
   * Par: how long an ordinary guest takes over this game, worked out from its own numbers
   * (`parFor`). Finishing on par scores 1000 points, whatever the game.
   */
  parMs: number;
}

export interface ThreadTask extends Base {
  type: 'THREAD';
  /** Vertical gap as a fraction of the field — smaller is harder. */
  gap: number;
  speed: number;
  /** How many gaps to clear. */
  target: number;
  theme: 'stars' | 'neon' | 'debris' | 'dust' | 'canopy';
}

export interface RhythmTask extends Base {
  type: 'RHYTHM';
  bpm: number;
  /** Which beats carry a pulse. */
  pattern: readonly boolean[];
  /**
   * How many tracks the pulses come down — left and right coils (three is possible, and was
   * too much). Each track runs at its own speed, so they cross the line out of step.
   */
  lanes: 1 | 2 | 3;
  /** Per-lane travel multiplier: below 1 is faster, above is slower. */
  laneSpeeds: readonly number[];
  /** The drive's beat drops out and you keep time through it. Off everywhere since 2026-09-29. */
  stutter: boolean;
  /** How many clean hits finish the task. */
  target: number;
  verb: string;
}

export interface VineTask extends Base {
  type: 'VINE';
  seeds: number;
  /** Milliseconds per step — lower is faster. */
  stepMs: number;
  /** Sealed beds bite back: hitting a wall costs you a seed. */
  walls: boolean;
  tint: string;
}

export interface EchoTask extends Base {
  type: 'ECHO';
  length: number;
  playbackMs: number;
  /** The signal arrives damaged and one step is unreadable. Off everywhere since 2026-09-29. */
  corrupted: boolean;
}

export interface RecallTask extends Base {
  type: 'RECALL';
  pairs: number;
  /** The archive is burning — unmatched cards start disappearing. Off everywhere since 2026-09-29. */
  burnAfterMs: number | null;
  glyphs: readonly string[];
}

export interface DivideTask extends Base {
  type: 'DIVIDE';
  /** How many fair shares to cut. */
  shares: number;
  /** How many things to cut up. */
  items: number;
  label2: string;
}

/**
 * 2617, under the sand: the board is dark, the finger carries a candle, and a few mushrooms grow
 * somewhere in the dark. Slide to look, tap one you can see to pick it. Nothing to get wrong.
 */
export interface ForageTask extends Base {
  type: 'FORAGE';
  /** How many mushrooms to pick. */
  count: number;
}

export interface RelayTask extends Base {
  type: 'RELAY';
}

/**
 * One of the thirty (thirty.ts): its board is its own component in the guest app, picked by
 * `game`, and plays in three rounds. (PASS IT ON, the one that was a RELAY, went on 2026-10-06.)
 */
export interface PlayTask extends Base {
  type: 'PLAY';
  game: ThirtyId;
  slot: Slot;
  labelNo: string;
}

export type TaskDef =
  ThreadTask | RhythmTask | VineTask | EchoTask | RecallTask | DivideTask | ForageTask | RelayTask | PlayTask;

type WithoutPar<T> = T extends unknown ? Omit<T, 'parMs'> : never;
/** A task as it is written in the table below, before its par is worked out from its numbers. */
export type TaskSpec = WithoutPar<TaskDef>;

/**
 * Every game is scored the same way, by how its time compares with its own par. Finish on par
 * and score 1000; twice as fast, 2000; each mistake counts as three more seconds:
 *
 *   points = round(1000 × par / (ms + mistakes × 3000)), kept between 100 and 2000
 *
 * The points are a leaderboard and nothing else. The drive is still floored by the wall clock
 * (charge.ts), so a slow table never holds the show up.
 */
export const SCORE = {
  PAR_POINTS: 1_000,
  MIN_POINTS: 100,
  MAX_POINTS: 2_000,
  MISTAKE_MS: 3_000,
  /** Nobody finishes a game faster than this, so a quicker claim is taken as this. */
  MIN_MS: 1_500,
  /** Ten minutes. A game left open longer is scored as if it took ten minutes. */
  MAX_MS: 600_000,
  MAX_MISTAKES: 50,
} as const;

/**
 * What an ordinary guest takes, one-handed and not hurrying, from the moment the game screen
 * opens to WELL DONE: a few seconds to read the board, the moves the game itself needs, and one
 * mistake wherever a mistake is possible. Every figure comes from the game's own settings, so a
 * harder board has a longer par and the scores stay fair between games. These are estimates
 * from how the games are built: once a dry run has real times (every finished game is in the
 * voyage log with its time and mistakes), tune the figures here and every par follows.
 */
export const PAR = {
  /** Reading the board before the first move. */
  SETTLE_MS: 3_000,
  /** One deliberate tap: a pad, a card. */
  TAP_MS: 600,
  /** One aimed cut across a bar. */
  CUT_MS: 1_000,
  /** DIVIDE: waiting for a sliding bar to bring its fair zone under the finger. */
  SWAY_MS: 900,
  /** THREAD: at this gap an ordinary flight clips about one gate; a smaller gap clips more. */
  THREAD_EASY_GAP: 0.35,
  /** RHYTHM: the share of pulses an ordinary guest catches, by how many coils there are. */
  RHYTHM_CATCH: [1, 0.85, 0.8, 0.75],
  /** RHYTHM: a stuttering drive loses about one pulse in seven to the silence. */
  RHYTHM_STUTTER: 7 / 6,
  /** VINE: how much further than the shortest way an ordinary steer goes... */
  VINE_DETOUR: 1.3,
  /** ...and the steps it takes to turn towards each new seed. */
  VINE_TURN_STEPS: 2,
  /** RECALL: turns per pair with ordinary memory. Perfect memory needs about 1.6. */
  RECALL_TURNS: 1.8,
  /** FORAGE: sweeping the candle round until one shows, and the tap to pick it. */
  FORAGE_FIND_MS: 2_500,
  /** RELAY: find the seat, show the code, hear it read back. */
  RELAY_MS: 25_000,
  /** Pars are kept to the half second. */
  ROUND_MS: 500,
} as const;

/** The average distance between two random cells along a side of `n`, with or without wrapping. */
function meanGap(n: number, wraps: boolean): number {
  if (!wraps) return (n * n - 1) / (3 * n);
  return n % 2 ? (n * n - 1) / (4 * n) : n / 4;
}

/** How many lights each ECHO round plays: the task's own length first, one more each round. */
export function echoLengths(task: Pick<EchoTask, 'length'>): number[] {
  return Array.from({ length: GAME_RULES.echo.rounds }, (_, r) => task.length + r);
}

/** A game's par, from its own numbers. */
export function parFor(task: TaskSpec): number {
  const { SETTLE_MS, TAP_MS, CUT_MS } = PAR;
  const mistake = SCORE.MISTAKE_MS;
  let ms: number;
  switch (task.type) {
    case 'THREAD': {
      const { tickMs, gateEvery, gateStartX, shipX } = GAME_RULES.thread;
      const gateMs = gateEvery * tickMs;
      const firstGateMs = (((gateStartX - shipX) * 100) / task.speed) * tickMs;
      // The game sets the pace. A clipped gate is a mistake, but it still counts as flown.
      const clipped = PAR.THREAD_EASY_GAP / task.gap;
      ms = firstGateMs + (task.target - 1) * gateMs + clipped * mistake;
      break;
    }
    case 'RHYTHM': {
      const beats = task.pattern.filter(Boolean).length;
      const pulseMs = (60_000 / task.bpm) * (task.pattern.length / Math.max(1, beats));
      const laneSpeed = task.laneSpeeds.reduce((sum, s) => sum + s, 0) / Math.max(1, task.laneSpeeds.length);
      const firstPulseMs = GAME_RULES.rhythm.travelMs * laneSpeed;
      const pulses =
        (task.target / (PAR.RHYTHM_CATCH[task.lanes] ?? 0.75)) * (task.stutter ? PAR.RHYTHM_STUTTER : 1);
      // A missed pulse just means waiting for the next; a tap on an empty coil is the mistake.
      ms = firstPulseMs + (pulses - 1) * pulseMs + mistake;
      break;
    }
    case 'VINE': {
      const { cols, rows } = GAME_RULES.vine;
      const wraps = !task.walls;
      const steps = (meanGap(cols, wraps) + meanGap(rows, wraps)) * PAR.VINE_DETOUR + PAR.VINE_TURN_STEPS;
      // Only a sealed bed has walls to bump, and a bump holds the vine up for a step or two.
      ms = task.seeds * steps * task.stepMs + (task.walls ? mistake + 2 * task.stepMs : 0);
      break;
    }
    case 'ECHO': {
      const { gapMs, leadMs, retryMs, rounds, roundSpeeds, roundPauseMs } = GAME_RULES.echo;
      const lengths = echoLengths(task);
      const playOf = (r: number) => leadMs + lengths[r]! * (task.playbackMs + gapMs) * (roundSpeeds[r] ?? 1);
      ms = lengths.reduce((sum, len, r) => sum + playOf(r) + len * TAP_MS, 0) + (rounds - 1) * roundPauseMs;
      // A wrong pad plays that round's signal again, after half an answer went for nothing:
      // reckoned on the longest round, where it happens.
      const last = rounds - 1;
      ms += mistake + retryMs + playOf(last) + (lengths[last]! / 2) * TAP_MS;
      break;
    }
    case 'RECALL': {
      const { flipBackMs, matchMs } = GAME_RULES.recall;
      const turns = PAR.RECALL_TURNS * task.pairs;
      let playMs = turns * 2 * TAP_MS + (turns - task.pairs) * flipBackMs + task.pairs * matchMs;
      // A burning archive loses a pair to the fire every so often, so the board runs out sooner.
      if (task.burnAfterMs !== null) playMs = task.pairs / (task.pairs / playMs + 1 / task.burnAfterMs);
      // The mistake is turning over a card already seen: one more wasted turn.
      ms = playMs + (2 * TAP_MS + flipBackMs + mistake);
      break;
    }
    case 'DIVIDE': {
      const cuts = task.items * (task.shares - 1);
      // A cut outside the fair zone is the mistake, and the cut is made again.
      ms = cuts * (CUT_MS + PAR.SWAY_MS) + (CUT_MS + PAR.SWAY_MS + mistake);
      break;
    }
    case 'FORAGE':
      // Nothing to get wrong: a tap in the dark only moves the candle.
      ms = task.count * PAR.FORAGE_FIND_MS;
      break;
    case 'RELAY':
      // Nothing to get wrong, and nothing on the board to read: it is all across the table.
      return PAR.RELAY_MS;
    case 'PLAY':
      // Three rounds of a board with its own numbers: the catalogue's estimate, reading included.
      return thirtyGame(task.game).parMs;
  }
  return Math.round((SETTLE_MS + ms) / PAR.ROUND_MS) * PAR.ROUND_MS;
}

function withPar(spec: TaskSpec): TaskDef {
  return { ...spec, parMs: parFor(spec) };
}

/** Beat patterns, plainest first. `true` is a pulse. */
const STEADY = [true, false, true, false, true, false, true, false] as const;
const SYNCOPATED = [true, false, true, true, false, true, false, true] as const;
const LIMPING = [true, false, false, true, true, false, true, false] as const;

/*
 * One set of easy numbers for every era (Marek, 2026-09-28), written out per game so an era can
 * still be tuned on its own after a dry run:
 *
 *   THREAD  gap 0.42+, slow gates, 6 of them        RHYTHM  two coils, 96 bpm, 6 hits
 *   VINE    300 ms a step, 4–5 seeds, no walls       ECHO    3–4 lights, slow
 *   RECALL  3–4 pairs, nothing burns                 DIVIDE  halves or thirds, 2–3 bars
 *   FORAGE  5 mushrooms                              RELAY   as it was
 */
const SPECS: Record<1 | 2 | 3 | 4 | 5, readonly TaskSpec[]> = {
  // Chapter 1 · 2026 — the mechanics start here and rotate one post per chapter.
  1: [
    {
      tag: 'NAV',
      label: 'Fly out of orbit',
      type: 'THREAD',
      hint: 'Tap to fly up. Fly through the gaps.',
      gap: 0.42,
      speed: 0.5,
      target: 6,
      theme: 'stars',
    },
    {
      tag: 'ENG',
      label: 'Start the engine',
      type: 'RHYTHM',
      hint: 'Both thumbs: tap the side whose light reaches the line.',
      bpm: 96,
      pattern: STEADY,
      lanes: 2,
      laneSpeeds: [1, 0.9],
      target: 6,
      stutter: false,
      verb: 'PRIME',
    },
    {
      tag: 'BIO',
      label: 'Grow the first plant',
      type: 'VINE',
      hint: 'Steer the plant to the seeds.',
      seeds: 4,
      stepMs: 300,
      walls: false,
      tint: '#6fb7ff',
    },
    {
      tag: 'SIG',
      label: 'Copy the launch signal',
      type: 'ECHO',
      hint: 'Watch the lights, then tap the same order.',
      length: 3,
      playbackMs: 600,
      corrupted: false,
    },
    {
      tag: 'ARC',
      label: 'Sort the last photos of Earth',
      type: 'RECALL',
      hint: 'Flip two cards. Find the pairs.',
      pairs: 3,
      burnAfterMs: null,
      glyphs: ['🜨', '☾', '★', '⌖', '❋', '⌘'],
    },
    {
      tag: 'QTM',
      label: 'Pour the first toast',
      type: 'DIVIDE',
      hint: 'Tap each bar in the middle.',
      shares: 2,
      items: 3,
      label2: 'THE FIRST POUR',
    },
  ],
  // Chapter 2 · 2213 — everything rotates one post along.
  2: [
    {
      tag: 'NAV',
      label: 'Keep the city’s beat',
      type: 'RHYTHM',
      hint: 'Both thumbs: tap the side whose light reaches the line.',
      bpm: 96,
      pattern: SYNCOPATED,
      lanes: 2,
      laneSpeeds: [1, 0.88],
      target: 6,
      stutter: false,
      verb: 'SYNC',
    },
    {
      tag: 'ENG',
      label: 'Connect the factory pipes',
      type: 'VINE',
      hint: 'Steer the pipe to the parts.',
      seeds: 5,
      stepMs: 300,
      walls: false,
      tint: '#ff4fd8',
    },
    {
      tag: 'BIO',
      label: 'Answer the garden',
      type: 'ECHO',
      hint: 'Watch the lights, then tap the same order.',
      length: 4,
      playbackMs: 560,
      corrupted: false,
    },
    {
      tag: 'SIG',
      label: 'Match the messages',
      type: 'RECALL',
      hint: 'Flip two cards. Find the pairs.',
      pairs: 4,
      burnAfterMs: null,
      glyphs: ['◉', '◈', '❂', '⬢', '✦', '◐', '⌬', '❖'],
    },
    {
      tag: 'ARC',
      label: 'Split the photo archive',
      type: 'DIVIDE',
      hint: 'Tap each bar where it splits into three.',
      shares: 3,
      items: 2,
      label2: 'THE RECORD',
    },
    {
      tag: 'QTM',
      label: 'Carry the drinks through the crowd',
      type: 'THREAD',
      hint: 'Tap to fly up. Fly through the gaps.',
      gap: 0.42,
      speed: 0.5,
      target: 6,
      theme: 'neon',
    },
  ],
  // Chapter 3 · 2398 — red alert. It looks meaner; it plays the same.
  3: [
    {
      tag: 'NAV',
      label: 'Reroute the power',
      type: 'VINE',
      hint: 'Steer the power line to the cells.',
      seeds: 5,
      stepMs: 300,
      walls: false,
      tint: '#ff2e3f',
    },
    {
      tag: 'ENG',
      label: 'Answer the engine',
      type: 'ECHO',
      hint: 'Watch the lights, then tap the same order.',
      length: 4,
      playbackMs: 560,
      corrupted: false,
    },
    {
      tag: 'BIO',
      label: 'Save the seeds',
      type: 'RECALL',
      hint: 'Flip two cards. Find the pairs.',
      pairs: 4,
      burnAfterMs: null,
      glyphs: ['✇', '⌸', '⍟', '⎈', '⏣', '☍', '⌾', '◍'],
    },
    {
      tag: 'SIG',
      label: 'Share the radio time',
      type: 'DIVIDE',
      hint: 'Tap each bar in the middle.',
      shares: 2,
      items: 3,
      label2: 'BANDWIDTH',
    },
    {
      tag: 'ARC',
      label: 'Carry the archive out',
      type: 'THREAD',
      hint: 'Tap to fly up. Fly through the gaps.',
      gap: 0.42,
      speed: 0.5,
      target: 6,
      theme: 'debris',
    },
    {
      tag: 'QTM',
      label: 'Keep the food line moving',
      type: 'RHYTHM',
      hint: 'Both thumbs: tap the side whose light reaches the line.',
      bpm: 96,
      pattern: STEADY,
      lanes: 2,
      laneSpeeds: [0.9, 1],
      target: 6,
      stutter: false,
      verb: 'HOLD IT',
    },
  ],
  // Chapter 4 · 2617 — under the sand, by hand, in the dark.
  4: [
    {
      tag: 'NAV',
      label: 'Call the caravan',
      type: 'ECHO',
      hint: 'Watch the lights, then tap the same order.',
      length: 4,
      playbackMs: 560,
      corrupted: false,
    },
    {
      tag: 'ENG',
      label: 'Match the spare parts',
      type: 'RECALL',
      hint: 'Flip two cards. Find the pairs.',
      pairs: 4,
      burnAfterMs: null,
      glyphs: ['⌾', '◍', '◇', '⬡', '✧', '⌑', '⍉', '⏥', '❍', '⌂'],
    },
    {
      // Replaced "Share the seeds" (cut bars into four), which nobody at the table understood.
      tag: 'BIO',
      label: 'Pick mushrooms in the dark',
      type: 'FORAGE',
      hint: 'Slide your finger to light the dark. Tap the mushrooms.',
      count: 5,
    },
    {
      tag: 'SIG',
      label: 'Send the signal through the tunnels',
      type: 'THREAD',
      hint: 'Tap to fly up. Fly through the gaps.',
      gap: 0.42,
      speed: 0.5,
      target: 6,
      theme: 'dust',
    },
    {
      tag: 'ARC',
      label: 'Play the market drum',
      type: 'RHYTHM',
      hint: 'Both thumbs: tap the side whose light reaches the line.',
      bpm: 96,
      pattern: LIMPING,
      lanes: 2,
      laneSpeeds: [1, 0.9],
      target: 6,
      stutter: false,
      verb: 'DRUM',
    },
    {
      tag: 'QTM',
      label: 'Find the trade route',
      type: 'VINE',
      hint: 'Steer the route to the stops.',
      seeds: 5,
      stepMs: 300,
      walls: false,
      tint: '#e08a3c',
    },
  ],
  // Chapter 5 · 2791 — the engines rest. Everything is slow and kind.
  5: [
    {
      tag: 'NAV',
      label: 'Match the flowers',
      type: 'RECALL',
      hint: 'Flip two cards. Find the pairs.',
      pairs: 4,
      burnAfterMs: null,
      glyphs: ['❦', '✿', '❀', '☘', '⚘', '❧', '✾', '❁', '✽', '❃'],
    },
    {
      tag: 'ENG',
      label: 'Share out the last power',
      type: 'DIVIDE',
      hint: 'Tap each bar in the middle.',
      shares: 2,
      items: 3,
      label2: 'THE LAST POUR',
    },
    {
      tag: 'BIO',
      label: 'Carry the seed through the trees',
      type: 'THREAD',
      hint: 'Tap to fly up. Fly through the gaps.',
      gap: 0.44,
      speed: 0.5,
      target: 6,
      theme: 'canopy',
    },
    {
      tag: 'SIG',
      label: 'Pass on the all-clear',
      type: 'RELAY',
      hint: 'Show your code to the guest named. They read it back. Then tap DONE.',
    },
    {
      tag: 'ARC',
      label: 'Grow the garden',
      type: 'VINE',
      hint: 'Steer the plant to the seeds.',
      seeds: 4,
      stepMs: 300,
      walls: false,
      tint: '#5c7d4e',
    },
    {
      tag: 'QTM',
      label: 'The last toast',
      type: 'ECHO',
      hint: 'Watch the lights, then tap the same order.',
      length: 3,
      playbackMs: 600,
      corrupted: false,
    },
  ],
};

/** The games, each with its par. */
export const TASKS: Record<1 | 2 | 3 | 4 | 5, readonly TaskDef[]> = {
  1: SPECS[1].map(withPar),
  2: SPECS[2].map(withPar),
  3: SPECS[3].map(withPar),
  4: SPECS[4].map(withPar),
  5: SPECS[5].map(withPar),
};

export const PROFESSIONS: readonly Profession[] = [
  'NAVIGATOR',
  'ENGINEER',
  'BIOTECHNICIAN',
  'SIGNAL OFFICER',
  'ARCHIVIST',
  'QUARTERMASTER',
];

export const PROFESSION_TAG: Record<Profession, ProfessionTag> = {
  NAVIGATOR: 'NAV',
  ENGINEER: 'ENG',
  BIOTECHNICIAN: 'BIO',
  'SIGNAL OFFICER': 'SIG',
  ARCHIVIST: 'ARC',
  QUARTERMASTER: 'QTM',
};

/**
 * The crew's names. In seat order (seat 1 is VEGA), because each is printed on the name card
 * at its own place and handed out with that seat at check-in: see roster.ts.
 */
export const CALLSIGNS: readonly string[] = [
  'VEGA',
  'KESTREL',
  'JUNO',
  'SABLE',
  'LYRA',
  'ORION-B',
  'HALCYON',
  'MIRA',
  'TALA',
  'CYGNUS',
  'ROOK',
  'EMBER',
];

/**
 * The thirty at each stop: every post's first holder's game (0–5, in post order), then every
 * post's second holder's (6–11). A second holder's game with nobody to play it is simply not
 * dealt (state.ts `dealGames`).
 */
const thirtyAt = (chapter: 1 | 2 | 3 | 4 | 5): readonly TaskDef[] =>
    (['A', 'B'] as const).flatMap((slot) =>
      THIRTY_POSTS.map((post): TaskDef => {
        const game = THIRTY[post][thirtyPlace(chapter, slot)]!;
        const [label, labelNo] = game.titles[chapter] ?? [game.name, game.nameNo];
        const base = { tag: post, label, labelNo, hint: game.hint, slot, game: game.id };
        return withPar({ ...base, type: 'PLAY' as const });
      }),
    );
const THIRTY_TASKS: Record<1 | 2 | 3 | 4 | 5, readonly TaskDef[]> = {
  1: thirtyAt(1),
  2: thirtyAt(2),
  3: thirtyAt(3),
  4: thirtyAt(4),
  5: thirtyAt(5),
};

/** A stop's games in a set: the classic six, or the thirty's twelve. */
export function tasksForChapter(chapter: Chapter, set: GameSet = 'classic'): readonly TaskDef[] {
  if (chapter === 0) return [];
  return set === 'thirty' ? THIRTY_TASKS[chapter] : TASKS[chapter];
}

export function taskDef(chapter: Chapter, index: number, set: GameSet = 'classic'): TaskDef | undefined {
  return tasksForChapter(chapter, set)[index];
}

/** A post's game at a stop — in the thirty, its first holder's unless `slot` says otherwise. */
export function taskIndexFor(chapter: Chapter, profession: Profession, set: GameSet = 'classic', slot: Slot = 'A'): number {
  const tag = PROFESSION_TAG[profession];
  return tasksForChapter(chapter, set).findIndex((t) => t.tag === tag && (t.slot === undefined || t.slot === slot));
}

export function taskKey(chapter: Chapter, index: number, seat: number): string {
  return `${chapter}-${index}-${seat}`;
}
